import React, { useState, useRef, useEffect } from 'react';
import { 
  X, 
  PenTool, 
  Stamp, 
  RotateCcw, 
  Trash2, 
  Check, 
  Sparkles, 
  Maximize2, 
  Palette, 
  Minus,
  AlertCircle
} from 'lucide-react';

const PEN_COLORS = [
  { id: 'navy', label: '딥 네이비', value: '#1e1b4b' },
  { id: 'black', label: '클래식 블랙', value: '#0f172a' },
  { id: 'red', label: '인주 적색', value: '#dc2626' }
];

const PEN_WIDTHS = [
  { id: 'thin', label: '얇게 (2.5px)', value: 2.5 },
  { id: 'medium', label: '보통 (4.0px)', value: 4.0 },
  { id: 'thick', label: '굵게 (6.0px)', value: 6.0 }
];

const SignaturePadModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  initialName = '',
  title = '고해상도 전자서명'
}) => {
  const [activeTab, setActiveTab] = useState('DRAW'); // 'DRAW' | 'STAMP'
  
  // Pen state
  const [penColor, setPenColor] = useState(PEN_COLORS[0].value);
  const [penWidth, setPenWidth] = useState(PEN_WIDTHS[1].value);
  const [hasDrawn, setHasDrawn] = useState(false);
  const [strokeHistory, setStrokeHistory] = useState([]);

  // Stamp state
  const [stampName, setStampName] = useState(initialName || '');
  const [stampShape, setStampShape] = useState('OVAL'); // 'OVAL' | 'CIRCLE' | 'SQUARE'

  const canvasRef = useRef(null);
  const isDrawingRef = useRef(false);
  const pointsRef = useRef([]);

  useEffect(() => {
    if (isOpen && activeTab === 'DRAW') {
      setTimeout(initCanvas, 50);
    }
  }, [isOpen, activeTab]);

  const initCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const dpr = Math.max(window.devicePixelRatio || 2, 2);

    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;

    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = penColor;
    ctx.lineWidth = penWidth;

    // Draw baseline guideline
    drawGuidelines(ctx, rect.width, rect.height);
  };

  const drawGuidelines = (ctx, width, height) => {
    ctx.save();
    ctx.strokeStyle = '#e2e8f0';
    ctx.lineWidth = 1;
    ctx.setLineDash([4, 4]);
    ctx.beginPath();
    ctx.moveTo(30, height * 0.75);
    ctx.lineTo(width - 30, height * 0.75);
    ctx.stroke();
    ctx.restore();
  };

  const getCanvasCoords = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();

    let clientX, clientY;
    if (e.touches && e.touches.length > 0) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }

    return {
      x: clientX - rect.left,
      y: clientY - rect.top
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    isDrawingRef.current = true;
    const pt = getCanvasCoords(e);
    pointsRef.current = [pt];

    // Save canvas state for Undo
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      const dpr = Math.max(window.devicePixelRatio || 2, 2);
      const snapshot = ctx.getImageData(0, 0, canvas.width, canvas.height);
      setStrokeHistory(prev => [...prev.slice(-10), snapshot]);
    }
  };

  const draw = (e) => {
    if (!isDrawingRef.current) return;
    e.preventDefault();

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    const pt = getCanvasCoords(e);
    pointsRef.current.push(pt);

    if (pointsRef.current.length > 2) {
      const pts = pointsRef.current;
      const lastPt = pts[pts.length - 1];
      const prevPt = pts[pts.length - 2];
      const prevPrevPt = pts[pts.length - 3];

      // Midpoint Bezier smoothing
      const midPoint1 = {
        x: (prevPrevPt.x + prevPt.x) / 2,
        y: (prevPrevPt.y + prevPt.y) / 2
      };
      const midPoint2 = {
        x: (prevPt.x + lastPt.x) / 2,
        y: (prevPt.y + lastPt.y) / 2
      };

      ctx.save();
      ctx.strokeStyle = penColor;
      ctx.lineWidth = penWidth;
      ctx.beginPath();
      ctx.moveTo(midPoint1.x, midPoint1.y);
      ctx.quadraticCurveTo(prevPt.x, prevPt.y, midPoint2.x, midPoint2.y);
      ctx.stroke();
      ctx.restore();

      setHasDrawn(true);
    }
  };

  const stopDrawing = (e) => {
    if (!isDrawingRef.current) return;
    e.preventDefault();
    isDrawingRef.current = false;
    pointsRef.current = [];
  };

  const handleClear = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const dpr = Math.max(window.devicePixelRatio || 2, 2);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    drawGuidelines(ctx, canvas.width / dpr, canvas.height / dpr);
    setHasDrawn(false);
    setStrokeHistory([]);
  };

  const handleUndo = () => {
    if (strokeHistory.length === 0) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    const prevSnapshot = strokeHistory[strokeHistory.length - 1];
    ctx.putImageData(prevSnapshot, 0, 0);
    setStrokeHistory(prev => prev.slice(0, -1));
    if (strokeHistory.length <= 1) {
      setHasDrawn(false);
    }
  };

  // Generate crisp stamp PNG data URL
  const generateStampPng = () => {
    const canvas = document.createElement('canvas');
    canvas.width = 160;
    canvas.height = 160;
    const ctx = canvas.getContext('2d');

    ctx.clearRect(0, 0, 160, 160);
    ctx.fillStyle = '#dc2626';
    ctx.strokeStyle = '#dc2626';
    ctx.lineWidth = 4;

    const text = (stampName.trim() || '서명').slice(0, 4);

    if (stampShape === 'OVAL') {
      ctx.beginPath();
      ctx.ellipse(80, 80, 56, 72, 0, 0, Math.PI * 2);
      ctx.stroke();
      
      // Inner line
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.ellipse(80, 80, 50, 66, 0, 0, Math.PI * 2);
      ctx.stroke();

      ctx.font = 'bold 26px serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(text, 80, 70);

      ctx.font = 'bold 15px sans-serif';
      ctx.fillText('(인)', 80, 106);
    } else if (stampShape === 'CIRCLE') {
      ctx.beginPath();
      ctx.arc(80, 80, 68, 0, Math.PI * 2);
      ctx.stroke();

      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.arc(80, 80, 62, 0, Math.PI * 2);
      ctx.stroke();

      ctx.font = 'bold 26px serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(text, 80, 72);

      ctx.font = 'bold 15px sans-serif';
      ctx.fillText('인', 80, 106);
    } else {
      // SQUARE
      ctx.strokeRect(16, 16, 128, 128);
      ctx.lineWidth = 1.5;
      ctx.strokeRect(22, 22, 116, 116);

      ctx.font = 'bold 26px serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(text, 80, 70);

      ctx.font = 'bold 15px sans-serif';
      ctx.fillText('직인', 80, 104);
    }

    return canvas.toDataURL('image/png');
  };

  const handleConfirm = () => {
    if (activeTab === 'DRAW') {
      if (!hasDrawn) {
        alert('캔버스에 서명을 작성해 주세요.');
        return;
      }
      const canvas = canvasRef.current;
      const dataUrl = canvas.toDataURL('image/png');
      onConfirm(dataUrl, 'DRAW');
    } else {
      if (!stampName.trim()) {
        alert('도장에 각인할 성명을 입력해 주세요.');
        return;
      }
      const dataUrl = generateStampPng();
      onConfirm(dataUrl, 'AUTO_STAMP');
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-slate-950/80 backdrop-blur-md flex items-center justify-center p-2 sm:p-4 overflow-y-auto text-left animate-in fade-in zoom-in-95 duration-150">
      <div className="bg-white rounded-3xl max-w-xl w-full flex flex-col shadow-2xl border border-slate-200 overflow-hidden">
        
        {/* Header */}
        <div className="px-5 py-4 bg-slate-900 text-white flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl bg-indigo-600 flex items-center justify-center text-white">
              <PenTool className="w-4 h-4" />
            </div>
            <div>
              <h2 className="text-sm md:text-base font-bold">{title}</h2>
              <p className="text-[11px] text-slate-400">부드러운 고해상도 자필 서명 또는 공인 전자도장을 생성합니다.</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-800 rounded-xl transition-colors cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tab Switch */}
        <div className="p-3 bg-slate-100 border-b border-slate-200 flex items-center gap-2">
          <button
            type="button"
            onClick={() => setActiveTab('DRAW')}
            className={`flex-1 py-2 px-3 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-1.5 cursor-pointer ${
              activeTab === 'DRAW'
                ? 'bg-white text-indigo-700 shadow-xs border border-indigo-200'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <PenTool className="w-3.5 h-3.5" />
            캔버스 자필 서명 (스마트 터치)
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('STAMP')}
            className={`flex-1 py-2 px-3 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-1.5 cursor-pointer ${
              activeTab === 'STAMP'
                ? 'bg-white text-rose-700 shadow-xs border border-rose-200'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <Stamp className="w-3.5 h-3.5" />
            공인 인감 / 도장 자동 생성
          </button>
        </div>

        {/* Content Area */}
        <div className="p-4 sm:p-5 space-y-4">
          {activeTab === 'DRAW' ? (
            <div className="space-y-3">
              {/* Pen Toolbar */}
              <div className="flex flex-wrap items-center justify-between gap-2 p-2 bg-slate-50 border border-slate-200 rounded-2xl">
                {/* Pen Colors */}
                <div className="flex items-center gap-1.5">
                  <span className="text-[10px] font-bold text-slate-500 mr-1">색상:</span>
                  {PEN_COLORS.map(c => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => {
                        setPenColor(c.value);
                        if (canvasRef.current) {
                          const ctx = canvasRef.current.getContext('2d');
                          ctx.strokeStyle = c.value;
                        }
                      }}
                      className={`w-6 h-6 rounded-full border-2 transition-transform cursor-pointer ${
                        penColor === c.value ? 'scale-110 border-indigo-600 ring-2 ring-indigo-200' : 'border-white shadow-xs'
                      }`}
                      style={{ backgroundColor: c.value }}
                      title={c.label}
                    />
                  ))}
                </div>

                {/* Pen Widths */}
                <div className="flex items-center gap-1">
                  <span className="text-[10px] font-bold text-slate-500 mr-1">굵기:</span>
                  {PEN_WIDTHS.map(w => (
                    <button
                      key={w.id}
                      type="button"
                      onClick={() => {
                        setPenWidth(w.value);
                        if (canvasRef.current) {
                          const ctx = canvasRef.current.getContext('2d');
                          ctx.lineWidth = w.value;
                        }
                      }}
                      className={`px-2 py-0.5 rounded-lg text-[10px] font-bold cursor-pointer transition-colors ${
                        penWidth === w.value ? 'bg-indigo-600 text-white' : 'bg-slate-200 text-slate-700 hover:bg-slate-300'
                      }`}
                    >
                      {w.label.split(' ')[0]}
                    </button>
                  ))}
                </div>

                {/* Undo / Clear Actions */}
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={handleUndo}
                    disabled={strokeHistory.length === 0}
                    className="p-1.5 bg-white border border-slate-200 rounded-lg text-slate-600 hover:text-slate-900 disabled:opacity-40 cursor-pointer shadow-2xs"
                    title="획 되돌리기 (Undo)"
                  >
                    <RotateCcw className="w-3.5 h-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={handleClear}
                    className="p-1.5 bg-white border border-slate-200 rounded-lg text-rose-600 hover:bg-rose-50 cursor-pointer shadow-2xs"
                    title="전체 지우기"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>

              {/* High-DPI Smooth Signature Canvas */}
              <div className="relative w-full h-56 md:h-64 bg-slate-50 border-2 border-dashed border-indigo-200 rounded-2xl overflow-hidden shadow-inner flex items-center justify-center touch-none">
                <canvas
                  ref={canvasRef}
                  onMouseDown={startDrawing}
                  onMouseMove={draw}
                  onMouseUp={stopDrawing}
                  onMouseLeave={stopDrawing}
                  onTouchStart={startDrawing}
                  onTouchMove={draw}
                  onTouchEnd={stopDrawing}
                  className="w-full h-full cursor-crosshair"
                />

                {!hasDrawn && (
                  <div className="absolute inset-0 pointer-events-none flex flex-col items-center justify-center text-slate-400 gap-1">
                    <PenTool className="w-6 h-6 text-indigo-400 opacity-60 animate-bounce" />
                    <span className="text-xs font-bold">여기에 터치하거나 마우스로 부드럽게 서명하세요</span>
                    <span className="text-[10px] text-slate-400">선명한 Retina 고해상도 베지에 곡선 스무딩 적용됨</span>
                  </div>
                )}
              </div>
            </div>
          ) : (
            /* Auto Stamp Generator View */
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                <div>
                  <label className="text-[11px] font-bold text-slate-700 block mb-1">도장 각인 성명 / 상호 *</label>
                  <input
                    type="text"
                    maxLength={4}
                    value={stampName}
                    onChange={e => setStampName(e.target.value)}
                    placeholder="예: 홍길동"
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-xl font-bold text-slate-900 focus:bg-white focus:outline-none focus:border-rose-600"
                  />
                  <span className="text-[10px] text-slate-400 mt-0.5 block">최대 4글자까지 인영이 깔끔하게 각인됩니다.</span>
                </div>

                <div>
                  <label className="text-[11px] font-bold text-slate-700 block mb-1">인장 형태 / 규격</label>
                  <select
                    value={stampShape}
                    onChange={e => setStampShape(e.target.value)}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-xl font-bold text-slate-900 focus:bg-white focus:outline-none focus:border-rose-600"
                  >
                    <option value="OVAL">타원형 개인 인감 (표준)</option>
                    <option value="CIRCLE">원형 성명인 (공인)</option>
                    <option value="SQUARE">사각형 직인 (법인)</option>
                  </select>
                </div>
              </div>

              {/* Stamp Live Preview */}
              <div className="p-6 bg-slate-50 border border-slate-200 rounded-2xl flex flex-col items-center justify-center space-y-2">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">실시간 인영(도장) 미리보기</span>
                <div className="p-3 bg-white border border-slate-200 rounded-2xl shadow-xs rotate-[-3deg] transition-transform hover:scale-105">
                  {stampShape === 'OVAL' ? (
                    <div className="w-16 h-20 rounded-[50%/60%] border-2 border-rose-600 flex flex-col items-center justify-center text-rose-600 font-bold text-xs bg-rose-50/40 p-1 shadow-2xs">
                      <span className="text-sm font-black">{stampName.slice(0, 4) || '서명'}</span>
                      <span className="text-[9px] font-bold mt-0.5">(인)</span>
                    </div>
                  ) : stampShape === 'CIRCLE' ? (
                    <div className="w-20 h-20 rounded-full border-2 border-rose-600 flex flex-col items-center justify-center text-rose-600 font-bold text-xs bg-rose-50/40 shadow-2xs">
                      <span className="text-base font-black">{stampName.slice(0, 4) || '서명'}</span>
                      <span className="text-[10px] font-bold">인</span>
                    </div>
                  ) : (
                    <div className="w-20 h-20 border-2 border-rose-600 flex flex-col items-center justify-center text-rose-600 font-bold text-xs bg-rose-50/40 shadow-2xs">
                      <span className="text-base font-black">{stampName.slice(0, 4) || '서명'}</span>
                      <span className="text-[10px] font-bold">직인</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="px-5 py-4 bg-slate-50 border-t border-slate-200 flex items-center justify-between">
          <span className="text-[11px] text-slate-500 font-medium hidden sm:inline">
            * 전자서명법 제3조에 따라 공인된 법적 효력을 갖습니다.
          </span>
          <div className="flex items-center gap-2 ml-auto">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-white border border-slate-300 hover:bg-slate-100 rounded-xl text-xs font-bold text-slate-700 cursor-pointer"
            >
              취소
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              className="px-5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-bold shadow-md cursor-pointer flex items-center gap-1.5 transition-transform active:scale-95"
            >
              <Check className="w-4 h-4" />
              <span>서명 완료 및 적용</span>
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};

export default SignaturePadModal;
